import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

interface TourOverlayProps {
  targetSelector: string;
  isActive: boolean;
}

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function TourOverlay({ targetSelector, isActive }: TourOverlayProps) {
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);

  useEffect(() => {
    if (!isActive || !targetSelector) {
      setTargetRect(null);
      return;
    }

    const updateRect = () => {
      // Find all matching elements and pick the first visible one
      const elements = document.querySelectorAll(targetSelector);
      let element: Element | null = null;
      
      for (const el of elements) {
        const style = window.getComputedStyle(el);
        const rect = el.getBoundingClientRect();
        // Check if element is visible (not hidden by CSS) AND has actual dimensions
        if (
          style.display !== 'none' && 
          style.visibility !== 'hidden' && 
          style.opacity !== '0' &&
          rect.width > 0 && 
          rect.height > 0
        ) {
          element = el;
          break;
        }
      }
      
      if (element) {
        const rect = element.getBoundingClientRect();
        const padding = 12; // Extra padding around the element
        setTargetRect({
          top: rect.top - padding,
          left: rect.left - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2,
        });
      } else {
        setTargetRect(null);
      }
    };

    // Initial update
    updateRect();

    // Update on scroll and resize
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);

    // Also observe DOM changes
    const observer = new MutationObserver(updateRect);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
      observer.disconnect();
    };
  }, [targetSelector, isActive]);

  if (!isActive) return null;

  const overlayBg = 'rgba(0, 0, 0, 0.75)';

  // If target not found, show full-screen dim overlay
  if (!targetRect) {
    return createPortal(
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-[99998] pointer-events-none"
        style={{ background: overlayBg }}
      />,
      document.body
    );
  }

  const overlayContent = (
    <AnimatePresence>
      {/* Top overlay - from top of screen to top of target */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed z-[99998] pointer-events-none"
        style={{
          top: 0,
          left: 0,
          right: 0,
          height: Math.max(0, targetRect.top),
          background: overlayBg,
        }}
      />

      {/* Bottom overlay - from bottom of target to bottom of screen */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed z-[99998] pointer-events-none"
        style={{
          top: targetRect.top + targetRect.height,
          left: 0,
          right: 0,
          bottom: 0,
          background: overlayBg,
        }}
      />

      {/* Left overlay - left side of target area */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed z-[99998] pointer-events-none"
        style={{
          top: targetRect.top,
          left: 0,
          width: Math.max(0, targetRect.left),
          height: targetRect.height,
          background: overlayBg,
        }}
      />

      {/* Right overlay - right side of target area */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed z-[99998] pointer-events-none"
        style={{
          top: targetRect.top,
          left: targetRect.left + targetRect.width,
          right: 0,
          height: targetRect.height,
          background: overlayBg,
        }}
      />
      
      {/* Bright spotlight border - pointer-events-none so it doesn't block */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.3 }}
        className="fixed z-[99999] pointer-events-none rounded-xl"
        style={{
          top: targetRect.top - 2,
          left: targetRect.left - 2,
          width: targetRect.width + 4,
          height: targetRect.height + 4,
          border: '3px solid hsl(211 100% 55%)',
          boxShadow: `
            0 0 0 3px hsl(211 100% 55% / 0.3),
            0 0 30px 6px hsl(211 100% 55% / 0.4),
            inset 0 0 15px hsl(211 100% 55% / 0.1)
          `,
          background: 'transparent',
        }}
      />

      {/* Pulsing ring animation */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ 
          opacity: [0.7, 0.3, 0.7],
          scale: [1, 1.06, 1],
        }}
        transition={{ 
          duration: 1.5,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        className="fixed z-[99997] pointer-events-none rounded-xl"
        style={{
          top: targetRect.top - 6,
          left: targetRect.left - 6,
          width: targetRect.width + 12,
          height: targetRect.height + 12,
          border: '2px solid hsl(211 100% 60%)',
          boxShadow: '0 0 15px hsl(211 100% 55% / 0.5)',
        }}
      />
    </AnimatePresence>
  );

  return createPortal(overlayContent, document.body);
}
